/*
 * <product-look> — "Complete the look" PDP block (snippets/product-look.liquid).
 * Checkbox list of the current product + add-ons; adds the checked set in one /cart/add.js call.
 * Savings preview mirrors the native Buy X Get Y discount: add-ons only, cheapest eligible first,
 * capped at offer get-quantity. Checkout is authoritative; this is display only.
 */
if (!customElements.get('product-look')) {
    class ProductLook extends HTMLElement {
        constructor() {
            super();
            // Main variant pickers live outside this element and set the form's id input programmatically.
            // setTimeout (not requestAnimationFrame): runs after the theme's synchronous change handlers, and
            // still runs in a background tab, where rAF is paused (left "This item" on a stale variant).
            this.onAnchorChange = () => setTimeout(() => this.update());
        }

        connectedCallback() {
            // Re-attached on every connect (disconnect removes it), even when already initialised.
            document.addEventListener('change', this.onAnchorChange);
            if (this.initialized) return;
            this.initialized = true;

            this.anchorForm = document.getElementById(this.dataset.anchorForm);
            this.anchorPrices = JSON.parse(this.querySelector('[data-look-anchor-prices]')?.textContent || '{}');
            this.offerRate = parseFloat(this.dataset.offerRate) || 0;
            this.offerGetQuantity = parseInt(this.dataset.offerGetQuantity, 10) || 0;
            this.submitButton = this.querySelector('[data-look-submit]');
            this.busy = false;

            this.addEventListener('change', this.onChange.bind(this));
            this.submitButton.addEventListener('click', this.onSubmit.bind(this));

            if (this.dataset.url) this.loadComplementary();
            this.update();
        }

        disconnectedCallback() {
            document.removeEventListener('change', this.onAnchorChange);
        }

        get pending() {
            return this.classList.contains('product-look--pending');
        }

        // While pending, add-ons are inert (CSS) and submit is disabled, so this swap can't discard choices.
        async loadComplementary() {
            try {
                const response = await fetch(this.dataset.url);
                const html = new DOMParser().parseFromString(await response.text(), 'text/html');
                const fresh = html.getElementById(this.id);
                if (fresh?.dataset.source === 'complementary') {
                    this.querySelector('[data-look-addons]').innerHTML = fresh.querySelector('[data-look-addons]').innerHTML;
                    this.dataset.source = 'complementary';
                }
            } catch (error) {
                console.error('product-look: complementary fetch failed', error);
            }
            this.classList.remove('product-look--pending');
            this.update();
        }

        onChange(event) {
            const item = event.target.closest('[data-look-item]');
            if (!item) return;
            if (event.target.matches('[data-look-toggle]')) item.classList.toggle('is-checked', event.target.checked);
            if (event.target.matches('select[data-look-variant]')) {
                this.setRowImage(item, event.target.selectedOptions[0]?.dataset.image);
            }
            this.update();
        }

        anchorVariantId() {
            return this.anchorForm?.querySelector('[name="id"]')?.value;
        }

        // [price, compare, available, imageSrcset] for the main form's selected variant; unavailable without a main form.
        anchorVariant() {
            return this.anchorPrices[this.anchorVariantId()] || [0, 0, false, ''];
        }

        /*
         * Show the selected variant's image in a row; a variant without its own image shows the product's
         * (data-default-image on the media wrapper). srcset strings come from snippets/product-look-srcset.liquid.
         */
        setRowImage(item, srcset) {
            const media = item.querySelector('.product-look__media');
            const img = media?.querySelector('img');
            const next = srcset || media?.dataset.defaultImage;
            if (!img || !next || img.getAttribute('srcset') === next) return;
            img.setAttribute('srcset', next);
            img.setAttribute('src', next.split(',').pop().trim().split(' ')[0]);
        }

        /*
         * A sold-out main variant can't be added, and /cart/add.js rejects the WHOLE items[] request when one
         * line fails — so drop the anchor (unchecked + disabled) and restore it when an available variant is picked.
         */
        syncAnchorAvailability(anchor) {
            const toggle = anchor.querySelector('[data-look-toggle]');
            const available = this.anchorForm && this.anchorVariant()[2];
            if (!available) {
                anchor.classList.add('is-soldout');
                anchor.classList.remove('is-checked');
                toggle.checked = false;
                toggle.disabled = true;
            } else if (anchor.classList.contains('is-soldout')) {
                anchor.classList.remove('is-soldout');
                anchor.classList.add('is-checked');
                toggle.checked = true;
                toggle.disabled = this.dataset.lockAnchor === 'true';
            }
        }

        // { price, compare } in cents for a row's selected variant; compare falls back to price (no sale).
        rowPricing(item) {
            let price;
            let compare;
            if (item.hasAttribute('data-look-anchor')) {
                [price, compare] = this.anchorVariant();
            } else {
                const input = item.querySelector('[data-look-variant]');
                const source = input.selectedOptions?.[0] || input;
                price = parseInt(source.dataset.price, 10) || 0;
                compare = parseInt(source.dataset.compare, 10) || 0;
            }
            return { price, compare: Math.max(compare, price) };
        }

        /*
         * Mirrors the native Buy X Get Y: when the anchor qualifies, the cheapest checked eligible add-ons
         * (up to get-quantity) get offerRate% off their CURRENT price (not retail), rounded DOWN per line like
         * Shopify (verified at checkout: $27.99 -> -$2.79). Returns Map(item -> cents off).
         */
        lookDiscounts(anchorChecked, checkedAddons) {
            const discounts = new Map();
            if (!(this.offerRate > 0 && anchorChecked && this.dataset.anchorEligible === 'true')) return discounts;
            checkedAddons
                .filter((item) => item.dataset.lookEligible === 'true')
                .sort((a, b) => this.rowPricing(a).price - this.rowPricing(b).price)
                .slice(0, this.offerGetQuantity)
                .forEach((item) => discounts.set(item, Math.floor(this.rowPricing(item).price * this.offerRate / 100)));
            return discounts;
        }

        update() {
            const anchor = this.querySelector('[data-look-anchor]');
            if (anchor) {
                this.syncAnchorAvailability(anchor);
                this.setRowImage(anchor, this.anchorVariant()[3]);
            }
            const anchorChecked = !!anchor && anchor.classList.contains('is-checked');
            const addons = [...this.querySelectorAll('[data-look-addons] [data-look-item]')];
            const checkedAddons = addons.filter((item) => item.classList.contains('is-checked'));
            const discounts = this.lookDiscounts(anchorChecked, checkedAddons);

            let total = 0;
            let retail = 0;
            let lookSaving = 0;
            [anchor, ...addons].filter(Boolean).forEach((item) => {
                const { price, compare } = this.rowPricing(item);
                const off = discounts.get(item) || 0;
                item.querySelector('[data-look-price]').innerHTML = this.priceHtml(price - off, compare);
                if (!item.classList.contains('is-checked')) return;
                total += price - off;
                retail += compare;
                lookSaving += off;
            });

            const count = checkedAddons.length + (anchorChecked ? 1 : 0);
            // Keep an empty block visible in the theme editor so the merchant can see and configure it.
            this.hidden = !addons.length && !this.pending && !window.Shopify?.designMode;
            this.querySelector('[data-look-total]').innerHTML = this.priceHtml(total, retail, 'price--medium');

            this.querySelector('[data-look-saving]').hidden = lookSaving <= 0;
            this.querySelector('[data-look-saving-value]').textContent = `-${this.money(lookSaving)}`;

            const { labelAll, labelCount, labelOne } = this.submitButton.dataset;
            const allChecked = count === addons.length + (anchor ? 1 : 0);
            this.submitButton.textContent = allChecked ? labelAll : (count === 1 ? labelOne : labelCount).replace('[count]', count);
            // busy: a change during an in-flight add must not re-enable the button (duplicate lines).
            this.submitButton.disabled = count === 0 || this.busy || this.pending;
        }

        /*
         * The .price markup snippets/price.liquid emits for the PDP (minus its blouse links, unit price and
         * "saved" line), so component-price.css owns colours, strikethrough and the -N% pill.
         * Keep classes/attributes in sync with price.liquid.
         */
        priceHtml(price, compare, sizeClass = '') {
            const onSale = compare > price;
            const showPercent = this.dataset.showPercent === 'true';
            const percent = onSale ? Math.round((compare - price) * 100 / compare) : 0;
            const amount = this.money(price);
            const pill = onSale && percent > 0 && showPercent
                ? `<dd class="price__label_sale price__label_percent"><span class="label_sale label_sale_percent">-${percent}%</span></dd>`
                : '';
            return `<div class="price ${sizeClass}${onSale ? ' price--on-sale' : ''}"><dl>`
                + `<div class="price__regular"><dd class="price__last"><span class="price-item price-item--regular">${amount}</span></dd></div>`
                + `<div class="price__sale">`
                + `<dd class="price__compare" data-compare="${compare}"><s class="price-item price-item--regular">${this.money(compare)}</s></dd>`
                + `<dd class="price__last${showPercent ? ' price_percent' : ''}" data-last="${price}"><span class="price-item price-item--sale">${amount}</span></dd>`
                + pill
                + `</div></dl></div>`;
        }

        lineItems() {
            const items = [];
            const anchor = this.querySelector('[data-look-anchor]');
            if (anchor?.classList.contains('is-checked') && this.anchorForm) {
                // Carry the main form's text line-item properties (e.g. Globo options). File inputs come out of
                // FormData as File objects, which JSON can't carry — those need the main Add to cart button.
                const properties = {};
                for (const [key, value] of new FormData(this.anchorForm)) {
                    const match = key.match(/^properties\[(.+)\]$/);
                    if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
                }
                items.push({ id: this.anchorVariantId(), quantity: 1, properties });
            }
            this.querySelectorAll('[data-look-addons] [data-look-item].is-checked').forEach((item) => {
                items.push({ id: item.querySelector('[data-look-variant]').value, quantity: 1 });
            });
            return items;
        }

        async onSubmit() {
            if (this.busy) return;
            const error = this.querySelector('[data-look-error]');
            error.hidden = true;

            const anchorChecked = this.querySelector('[data-look-anchor]')?.classList.contains('is-checked');
            if (anchorChecked && this.anchorForm && !this.anchorForm.checkValidity()) {
                this.anchorForm.reportValidity();
                return;
            }

            this.busy = true;
            this.submitButton.classList.add('is-loading');
            this.update();
            try {
                const response = await fetch(`${window.routes.cart_add_url}.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ items: this.lineItems() })
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.description || result.message);
                await this.afterAdd();
            } catch (e) {
                error.textContent = e.message || window.cartStrings?.error || 'Error';
                error.hidden = false;
            } finally {
                this.busy = false;
                this.submitButton.classList.remove('is-loading');
                this.update();
            }
        }

        // Follows the theme's after-add settings (theme.js actionAddToCart): cart page, or quick cart on/off.
        async afterAdd() {
            if (window.after_add_to_cart?.type === 'cart') {
                window.location.href = window.routes.cart;
                return;
            }
            const cart = await (await fetch(`${window.routes.cart}.js`)).json();
            window.updateCart?.(cart);
            if (window.quick_cart?.show === false) {
                if (typeof showWarning === 'function') showWarning(window.after_add_to_cart?.message_2);
                return;
            }
            document.body.classList.add('cart-sidebar-show');
        }

        money(cents) {
            return Shopify.formatMoney(cents, window.money_format);
        }
    }

    customElements.define('product-look', ProductLook);
}
